import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import { PersonTypeEnum } from '@clickersphere/core/enums/PersonTypeEnum';
import {
  applyDocumentMask,
  stripDocumentMask,
} from '@clickersphere/core/utils/documentMask';
import { useCreateContractor } from '../../../../app/hooks/contractor/useCreateContractor';
import { useUpdateContractor } from '../../../../app/hooks/contractor/useUpdateContractor';
import { useError } from '../../../../app/hooks/useError';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';
import {
  toContractAddressFormValues,
  toContractAddressPayload,
} from '../../../../app/utils/contractAddressForm';

function trimToUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

export function useContractorModalController() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { handleError } = useError();
  const { contractor, handleCloseModal } = useStore(
    useShallow((state) => ({
      contractor: state.modal.contractor,
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );
  const { createContractorAsync, createContractorIsPending } =
    useCreateContractor();
  const { updateContractorAsync, updateContractorIsPending } =
    useUpdateContractor();

  const schema = z
    .object({
      personType: z.enum(PersonTypeEnum),
      name: z
        .string()
        .trim()
        .nonempty(t('contract.contractor.validation.nameRequired'))
        .max(100, t('contract.contractor.validation.nameMaxLength')),
      documentNumber: z.string(),
      legalRepresentativeName: z
        .string()
        .trim()
        .max(
          100,
          t('contract.contractor.validation.legalRepresentativeNameMaxLength'),
        ),
      legalRepresentativeDocument: z.string(),
      email: z
        .string()
        .max(100, t('contract.contractor.validation.emailInvalid'))
        .refine(
          (value) => value === '' || z.email().safeParse(value).success,
          t('contract.contractor.validation.emailInvalid'),
        ),
      phone: z
        .string()
        .max(30, t('contract.contractor.validation.phoneMaxLength')),
      instagram: z
        .string()
        .max(30, t('contract.contractor.validation.instagramMaxLength')),
      address: z.object({
        street: z.string(),
        number: z.string(),
        complement: z.string(),
        neighborhood: z.string(),
        city: z.string(),
        state: z.string(),
        zipCode: z.string(),
      }),
    })
    .superRefine((data, ctx) => {
      const documentDigits = stripDocumentMask(data.documentNumber);

      if (
        data.personType === PersonTypeEnum.PF &&
        documentDigits.length !== 11
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['documentNumber'],
          message: t('contract.contractor.validation.documentCpfInvalid'),
        });
      }

      if (
        data.personType === PersonTypeEnum.PJ &&
        documentDigits.length !== 14
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['documentNumber'],
          message: t('contract.contractor.validation.documentCnpjInvalid'),
        });
      }

      if (data.personType === PersonTypeEnum.PJ) {
        const representativeDigits = stripDocumentMask(
          data.legalRepresentativeDocument,
        );
        if (
          representativeDigits.length > 0 &&
          representativeDigits.length !== 11
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['legalRepresentativeDocument'],
            message: t(
              'contract.contractor.validation.legalRepresentativeDocumentInvalid',
            ),
          });
        }
      }
    });

  type FormData = z.infer<typeof schema>;

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      personType: contractor?.personType ?? PersonTypeEnum.PF,
      name: contractor?.name ?? '',
      documentNumber: contractor?.documentNumber
        ? applyDocumentMask(contractor.documentNumber)
        : '',
      legalRepresentativeName: contractor?.legalRepresentativeName ?? '',
      legalRepresentativeDocument: contractor?.legalRepresentativeDocument
        ? applyDocumentMask(contractor.legalRepresentativeDocument)
        : '',
      email: contractor?.email ?? '',
      phone: contractor?.phone ?? '',
      instagram: contractor?.instagram ?? '',
      address: toContractAddressFormValues(contractor?.address),
    },
  });

  const {
    handleSubmit: hookFormHandleSubmit,
    register,
    control,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const personType = watch('personType');
  const isLegalEntity = personType === PersonTypeEnum.PJ;

  function handlePersonTypeChange(value: string) {
    setValue('personType', value as PersonTypeEnum, { shouldDirty: true });
  }

  const handleSubmit = hookFormHandleSubmit(async (data) => {
    const addressPayload = toContractAddressPayload(data.address);
    const isPj = data.personType === PersonTypeEnum.PJ;
    const basePayload = {
      personType: data.personType,
      name: data.name.trim(),
      documentNumber: stripDocumentMask(data.documentNumber),
      legalRepresentativeName: isPj
        ? trimToUndefined(data.legalRepresentativeName)
        : undefined,
      legalRepresentativeDocument: isPj
        ? trimToUndefined(stripDocumentMask(data.legalRepresentativeDocument))
        : undefined,
      email: trimToUndefined(data.email),
      phone: trimToUndefined(data.phone),
      instagram: trimToUndefined(data.instagram),
    };

    try {
      if (!contractor) {
        await createContractorAsync({
          ...basePayload,
          address: addressPayload,
        });
        toast(t('contract.contractor.toast.created'), 'success');
      } else {
        await updateContractorAsync({
          id: contractor.id,
          data: { ...basePayload, address: addressPayload ?? null },
        });
        toast(t('contract.contractor.toast.updated'), 'success');
      }
      handleCloseModal();
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
        return;
      }
      toast(
        contractor
          ? t('contract.contractor.toast.error.update')
          : t('contract.contractor.toast.error.create'),
        'error',
      );
    }
  });

  return {
    t,
    form,
    register,
    control,
    errors,
    personType,
    isLegalEntity,
    isEditing: !!contractor,
    isFetching: createContractorIsPending || updateContractorIsPending,
    handlePersonTypeChange,
    handleSubmit,
    handleCloseModal,
  };
}
